import type { FloraCatalog } from '@/flora/FloraCatalog';
import type { ContainerStageId, FloraCategory, FloraDefinition } from '@/flora/FloraDefinition';
import type { FloraInstance } from '@/flora/FloraInstance';
import type { PlantGroup } from '@/editor/GroupManager';
import { formFactorLabel, formFactorOf, type FormFactor } from './FloraFacets';
import { computePlantMetrics } from './PlantMetrics';
import { mixHex } from './ChronoPalette';
import type { DisplayUnit } from './UiPreferences';

/**
 * The plant manifest: what is actually planted on this site, as a document rather than as a scene.
 *
 * A landscape drawing is only half the deliverable — the other half is the schedule: every specimen
 * counted, named, and sized, so it can be ordered from a nursery and checked off on installation.
 * This module derives that from canonical data, the same way `PlantMetrics` derives the inspector's
 * figures, so the report can never disagree with what the viewport shows.
 *
 * Deliberately DOM-free and pure. `PlantManifestPanel` renders it, `PlantMap` plots it, CSV export
 * serialises it, and the tests exercise all three through this one structure.
 */

/** One placed specimen, flattened into the fields a schedule line needs. */
export interface ManifestPlant {
  id: string;
  /** 1-based position in the manifest's stable ordering — the tag number on a plan. */
  index: number;
  definitionId: string;
  botanicalName: string;
  /** Botanical name plus cultivar in the conventional `Genus species 'Cultivar'` form. */
  fullName: string;
  commonName: string;
  cultivar: string | null;
  category: FloraCategory;
  formFactor: FormFactor;
  /** World position. `y` is dropped: a schedule is read in plan. */
  x: number;
  z: number;
  heightMeters: number;
  spreadMeters: number;
  /** 0–1+, where 1 is typical maturity. */
  maturity: number;
  ageYears: number;
  /** Nursery container this specimen is closest to — the size you would order it at. */
  containerStage: ContainerStageId | null;
  /** 0–1. Below `AILING_HEALTH` the plant is called out in the report. */
  health: number;
  canopyAreaSquareMeters: number;
  groupName: string | null;
  locked: boolean;
  hidden: boolean;
}

/** A species rollup — one line of the plant schedule. */
export interface ManifestSpecies {
  definitionId: string;
  botanicalName: string;
  fullName: string;
  commonName: string;
  cultivar: string | null;
  category: FloraCategory;
  formFactor: FormFactor;
  /** Plot/legend colour, stable for a given set of species. See {@link assignManifestColors}. */
  color: string;
  count: number;
  /** Ids of the placed specimens, in manifest order — what a row click selects. */
  plantIds: string[];
  minHeightMeters: number;
  maxHeightMeters: number;
  averageMaturity: number;
  matureHeightMeters: number;
  matureSpreadMeters: number;
  recommendedSpacingMeters: number;
  /** Canopy the species covers today, summed over its specimens. */
  canopyAreaSquareMeters: number;
  /** Canopy it will cover at typical maturity — the number that reveals overplanting. */
  matureCanopyAreaSquareMeters: number;
  /** The container size most of these specimens are closest to, for the order. */
  commonContainerStage: ContainerStageId | null;
  hiddenCount: number;
  ailingCount: number;
}

/** Schedule section — trees, shrubs, grasses… the order a plant list is conventionally read in. */
export interface ManifestSection {
  formFactor: FormFactor;
  label: string;
  species: ManifestSpecies[];
  count: number;
  canopyAreaSquareMeters: number;
}

export interface PlantManifest {
  plants: ManifestPlant[];
  /** Every species present, sorted by section then botanical name. */
  species: ManifestSpecies[];
  sections: ManifestSection[];
  totalPlants: number;
  totalSpecies: number;
  canopyAreaSquareMeters: number;
  matureCanopyAreaSquareMeters: number;
  hiddenCount: number;
  lockedCount: number;
  ailingCount: number;
  /** Species id → plot colour, for anything drawing alongside the schedule. */
  colors: Map<string, string>;
}

export interface BuildManifestOptions {
  instances: readonly FloraInstance[];
  catalog: FloraCatalog;
  /** Named groups, so a specimen can report the group it belongs to. */
  groups?: readonly PlantGroup[];
}

/** Health at or below this reads as a plant in trouble, and is called out in the report. */
export const AILING_HEALTH = 0.6;

/* ----------------------------------- Plot colours ----------------------------------- */

/**
 * Plot colours for the schedule swatches and the plan map.
 *
 * Not the ID-map palette from `capture/SpeciesPalette.ts`: that one is tuned to survive a PNG round
 * trip through a vision model, which makes it muddy to read. These are chosen to stay separable to
 * a human eye at swatch size, over both HUD themes.
 */
export const MANIFEST_PALETTE: readonly string[] = [
  '#3f8f45',
  '#4a7fd4',
  '#d9a12c',
  '#c0533f',
  '#8a63d2',
  '#2fa3b8',
  '#89b03a',
  '#d1749c',
  '#b0703a',
  '#5f7f96',
  '#43a06b',
  '#9c7f2f',
  '#7a5fa8',
  '#cf6f4a',
  '#3f8f8a',
  '#a8477a',
];

/** Base palette, then a lighter pass, then a darker one — 48 separable colours before any repeat. */
const COLOR_CYCLES = 3;

function colorAtSlot(slot: number): string {
  const base = MANIFEST_PALETTE[slot % MANIFEST_PALETTE.length] as string;
  const cycle = Math.floor(slot / MANIFEST_PALETTE.length) % COLOR_CYCLES;
  if (cycle === 1) return mixHex(base, '#ffffff', 0.36);
  if (cycle === 2) return mixHex(base, '#101418', 0.34);
  return base;
}

/** FNV-1a, matching `SpeciesPalette` — the same colour for a species on every machine and run. */
function hashId(text: string): number {
  let value = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    value ^= text.charCodeAt(i);
    value = (value + ((value << 1) + (value << 4) + (value << 7) + (value << 8) + (value << 24))) >>> 0;
  }
  return value >>> 0;
}

/**
 * Assign a plot colour per species id.
 *
 * Hash to a preferred slot, probe forward on a collision. Sorting the ids first makes the result
 * depend only on the *set* of species, so reopening the manifest on an unchanged site always
 * produces the same legend. Past 48 species colours start repeating rather than throwing: a report
 * with a duplicate swatch is a minor annoyance, a report that refuses to open is not.
 */
export function assignManifestColors(definitionIds: readonly string[]): Map<string, string> {
  const total = MANIFEST_PALETTE.length * COLOR_CYCLES;
  const unique = [...new Set(definitionIds)].sort();
  const taken = new Set<number>();
  const colors = new Map<string, string>();

  for (const id of unique) {
    let slot = hashId(id) % total;
    for (let probe = 0; probe < total && taken.has(slot); probe += 1) {
      slot = (slot + 1) % total;
    }
    taken.add(slot);
    colors.set(id, colorAtSlot(slot));
  }
  return colors;
}

/* ------------------------------------ The build ------------------------------------ */

/** `Genus species 'Cultivar'` — how a plant is written on a schedule. */
export function fullBotanicalName(definition: FloraDefinition): string {
  return definition.cultivar
    ? `${definition.botanicalName} '${definition.cultivar}'`
    : definition.botanicalName;
}

const SECTION_ORDER: readonly FormFactor[] = ['tree', 'shrub', 'grass', 'perennial', 'groundcover'];

/**
 * Roll the placed instances up into a manifest.
 *
 * Instances whose definition is missing from the catalog are skipped rather than faulted: a site
 * saved against a catalog that has since dropped a record should still produce a report for
 * everything else in it.
 */
export function buildPlantManifest(options: BuildManifestOptions): PlantManifest {
  const { instances, catalog } = options;
  const groupNames = new Map<string, string>();
  for (const group of options.groups ?? []) groupNames.set(group.id, group.name);

  const plants: ManifestPlant[] = [];
  const byDefinition = new Map<string, { definition: FloraDefinition; plants: ManifestPlant[] }>();

  // Sorted by species then position so the tag numbers are stable across sessions: instance
  // insertion order is an editing accident, and a schedule that renumbers on reload is useless.
  const ordered = [...instances].sort(compareForNumbering);

  for (const instance of ordered) {
    const definition = catalog.find(instance.floraDefinitionId);
    if (!definition) continue;

    const metrics = computePlantMetrics(definition, instance);
    const plant: ManifestPlant = {
      id: instance.id,
      index: plants.length + 1,
      definitionId: definition.id,
      botanicalName: definition.botanicalName,
      fullName: fullBotanicalName(definition),
      commonName: definition.commonNames[0] ?? definition.botanicalName,
      cultivar: definition.cultivar ?? null,
      category: definition.category,
      formFactor: formFactorOf(definition),
      x: instance.position.x,
      z: instance.position.z,
      heightMeters: metrics.growth.heightMeters,
      spreadMeters: metrics.growth.spreadMeters,
      maturity: instance.maturity,
      ageYears: metrics.approximateAgeYears,
      containerStage: metrics.nearestContainerStage?.id ?? null,
      health: instance.health,
      canopyAreaSquareMeters: metrics.canopyAreaSquareMeters,
      groupName: instance.groupId ? (groupNames.get(instance.groupId) ?? null) : null,
      locked: instance.locked,
      hidden: instance.hidden,
    };
    plants.push(plant);

    const bucket = byDefinition.get(definition.id);
    if (bucket) bucket.plants.push(plant);
    else byDefinition.set(definition.id, { definition, plants: [plant] });
  }

  const colors = assignManifestColors([...byDefinition.keys()]);
  const species = [...byDefinition.values()]
    .map(({ definition, plants: members }) =>
      summariseSpecies(definition, members, colors.get(definition.id) ?? MANIFEST_PALETTE[0] ?? '#3f8f45'),
    )
    .sort(compareSpecies);

  const sections: ManifestSection[] = [];
  for (const formFactor of SECTION_ORDER) {
    const members = species.filter((entry) => entry.formFactor === formFactor);
    if (members.length === 0) continue;
    sections.push({
      formFactor,
      label: formFactorLabel(formFactor),
      species: members,
      count: sum(members, (entry) => entry.count),
      canopyAreaSquareMeters: sum(members, (entry) => entry.canopyAreaSquareMeters),
    });
  }

  return {
    plants,
    species,
    sections,
    totalPlants: plants.length,
    totalSpecies: species.length,
    canopyAreaSquareMeters: sum(species, (entry) => entry.canopyAreaSquareMeters),
    matureCanopyAreaSquareMeters: sum(species, (entry) => entry.matureCanopyAreaSquareMeters),
    hiddenCount: plants.filter((plant) => plant.hidden).length,
    lockedCount: plants.filter((plant) => plant.locked).length,
    ailingCount: plants.filter((plant) => plant.health <= AILING_HEALTH).length,
    colors,
  };
}

function summariseSpecies(
  definition: FloraDefinition,
  members: ManifestPlant[],
  color: string,
): ManifestSpecies {
  const matureSpread = definition.growth.matureSpreadMeters.typical;
  const matureRadius = matureSpread / 2;
  const stages = new Map<ContainerStageId, number>();
  for (const plant of members) {
    if (!plant.containerStage) continue;
    stages.set(plant.containerStage, (stages.get(plant.containerStage) ?? 0) + 1);
  }
  let commonContainerStage: ContainerStageId | null = null;
  let best = 0;
  for (const [stage, count] of stages) {
    if (count > best) {
      best = count;
      commonContainerStage = stage;
    }
  }

  return {
    definitionId: definition.id,
    botanicalName: definition.botanicalName,
    fullName: fullBotanicalName(definition),
    commonName: definition.commonNames[0] ?? definition.botanicalName,
    cultivar: definition.cultivar ?? null,
    category: definition.category,
    formFactor: formFactorOf(definition),
    color,
    count: members.length,
    plantIds: members.map((plant) => plant.id),
    // Folded rather than spread through `Math.min`: a mass planting can hold tens of thousands of
    // specimens, which is past the argument limit on every engine.
    minHeightMeters: members.reduce((low, plant) => Math.min(low, plant.heightMeters), Infinity),
    maxHeightMeters: members.reduce((high, plant) => Math.max(high, plant.heightMeters), -Infinity),
    averageMaturity: sum(members, (plant) => plant.maturity) / Math.max(1, members.length),
    matureHeightMeters: definition.growth.matureHeightMeters.typical,
    matureSpreadMeters: matureSpread,
    recommendedSpacingMeters: definition.placement.recommendedSpacingMeters,
    canopyAreaSquareMeters: sum(members, (plant) => plant.canopyAreaSquareMeters),
    matureCanopyAreaSquareMeters: Math.PI * matureRadius * matureRadius * members.length,
    commonContainerStage,
    hiddenCount: members.filter((plant) => plant.hidden).length,
    ailingCount: members.filter((plant) => plant.health <= AILING_HEALTH).length,
  };
}

function compareForNumbering(a: FloraInstance, b: FloraInstance): number {
  if (a.floraDefinitionId !== b.floraDefinitionId) {
    return a.floraDefinitionId.localeCompare(b.floraDefinitionId);
  }
  if (a.position.x !== b.position.x) return a.position.x - b.position.x;
  if (a.position.z !== b.position.z) return a.position.z - b.position.z;
  return a.id.localeCompare(b.id);
}

function compareSpecies(a: ManifestSpecies, b: ManifestSpecies): number {
  const sectionDelta = SECTION_ORDER.indexOf(a.formFactor) - SECTION_ORDER.indexOf(b.formFactor);
  if (sectionDelta !== 0) return sectionDelta;
  return a.fullName.localeCompare(b.fullName);
}

function sum<T>(items: readonly T[], of: (item: T) => number): number {
  let total = 0;
  for (const item of items) total += of(item);
  return total;
}

/* --------------------------------- Sorting the views --------------------------------- */

export type ScheduleSort = 'section' | 'quantity' | 'name' | 'size';

export const SCHEDULE_SORTS: readonly { value: ScheduleSort; label: string }[] = [
  { value: 'section', label: 'Plant type' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'name', label: 'Botanical name' },
  { value: 'size', label: 'Mature size' },
];

/** Sort a species list for the schedule view. `section` keeps the manifest's own ordering. */
export function sortSpecies(
  species: readonly ManifestSpecies[],
  sort: ScheduleSort,
): ManifestSpecies[] {
  const list = [...species];
  switch (sort) {
    case 'quantity':
      return list.sort((a, b) => b.count - a.count || a.fullName.localeCompare(b.fullName));
    case 'name':
      return list.sort((a, b) => a.fullName.localeCompare(b.fullName));
    case 'size':
      return list.sort(
        (a, b) => b.matureHeightMeters - a.matureHeightMeters || a.fullName.localeCompare(b.fullName),
      );
    case 'section':
      return list.sort(compareSpecies);
  }
}

/** Free-text match over the names a designer would search by. */
export function matchesQuery(plant: ManifestPlant, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return (
    plant.fullName.toLowerCase().includes(needle) ||
    plant.commonName.toLowerCase().includes(needle) ||
    plant.definitionId.toLowerCase().includes(needle) ||
    (plant.groupName?.toLowerCase().includes(needle) ?? false) ||
    plant.containerStage?.toLowerCase() === needle
  );
}

/* ------------------------------------ Formatting ------------------------------------ */

const SQUARE_FEET_PER_SQUARE_METER = 10.7639;

/** Area in the reading unit — canopy cover is quoted in square metres or square feet, never both. */
export function formatArea(squareMeters: number, unit: DisplayUnit): string {
  const value = unit === 'feet' ? squareMeters * SQUARE_FEET_PER_SQUARE_METER : squareMeters;
  const suffix = unit === 'feet' ? 'ft²' : 'm²';
  if (value < 10) return `${value.toFixed(1)} ${suffix}`;
  if (value < 10000) return `${Math.round(value)} ${suffix}`;
  return `${(value / 1000).toFixed(1)}k ${suffix}`;
}

/** Nursery container labels — `3gal` is a field abbreviation, not something to print in a report. */
const CONTAINER_LABELS: Record<ContainerStageId, string> = {
  '1gal': '#1 container',
  '2gal': '#2 container',
  '3gal': '#3 container',
  '5gal': '#5 container',
  '7gal': '#7 container',
  '15gal': '#15 container',
  field: 'B&B / field-grown',
  mature: 'Mature specimen',
};

export function containerLabel(stage: ContainerStageId | null): string {
  return stage ? CONTAINER_LABELS[stage] : '—';
}

/* --------------------------------------- CSV --------------------------------------- */

const FEET_PER_METER = 3.28084;

function inUnit(meters: number, unit: DisplayUnit): number {
  return unit === 'feet' ? meters * FEET_PER_METER : meters;
}

function round(value: number, places = 2): string {
  return (Math.round(value * 10 ** places) / 10 ** places).toFixed(places);
}

/**
 * Escape one CSV cell.
 *
 * Botanical names carry apostrophes and commas ("Acer palmatum 'Bloodgood', red"), and a leading
 * `=` or `+` in a name is a formula injection waiting for whoever opens the file — so anything
 * suspicious is quoted and prefixed with a single quote.
 */
function cell(value: string | number): string {
  const text = String(value);
  const risky = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(risky) ? `"${risky.replace(/"/g, '""')}"` : risky;
}

function toCsv(rows: readonly (string | number)[][]): string {
  return rows.map((row) => row.map(cell).join(',')).join('\r\n');
}

/** The plant schedule as CSV — one line per species, the sheet a nursery is sent. */
export function scheduleCsv(
  species: readonly ManifestSpecies[],
  unit: DisplayUnit,
): string {
  const length = unit === 'feet' ? 'ft' : 'm';
  const area = unit === 'feet' ? 'ft²' : 'm²';
  const rows: (string | number)[][] = [
    [
      'Qty',
      'Botanical name',
      'Common name',
      'Type',
      'Specified size',
      `Height now (${length})`,
      `Mature height (${length})`,
      `Mature spread (${length})`,
      `Spacing (${length})`,
      `Canopy now (${area})`,
      `Canopy at maturity (${area})`,
      'Species id',
    ],
  ];
  for (const entry of species) {
    rows.push([
      entry.count,
      entry.fullName,
      entry.commonName,
      formFactorLabel(entry.formFactor),
      containerLabel(entry.commonContainerStage),
      `${round(inUnit(entry.minHeightMeters, unit), 1)}–${round(inUnit(entry.maxHeightMeters, unit), 1)}`,
      round(inUnit(entry.matureHeightMeters, unit), 1),
      round(inUnit(entry.matureSpreadMeters, unit), 1),
      round(inUnit(entry.recommendedSpacingMeters, unit), 1),
      round(areaInUnit(entry.canopyAreaSquareMeters, unit), 1),
      round(areaInUnit(entry.matureCanopyAreaSquareMeters, unit), 1),
      entry.definitionId,
    ]);
  }
  return toCsv(rows);
}

/** Every specimen as CSV — the take-off list, one line per plant. */
export function plantsCsv(plants: readonly ManifestPlant[], unit: DisplayUnit): string {
  const length = unit === 'feet' ? 'ft' : 'm';
  const rows: (string | number)[][] = [
    [
      'Tag',
      'Botanical name',
      'Common name',
      `X (${length})`,
      `Z (${length})`,
      `Height (${length})`,
      `Spread (${length})`,
      'Maturity %',
      'Age (years)',
      'Specified size',
      'Health %',
      'Group',
      'Locked',
      'Hidden',
      'Instance id',
    ],
  ];
  for (const plant of plants) {
    rows.push([
      plant.index,
      plant.fullName,
      plant.commonName,
      round(inUnit(plant.x, unit), 2),
      round(inUnit(plant.z, unit), 2),
      round(inUnit(plant.heightMeters, unit), 2),
      round(inUnit(plant.spreadMeters, unit), 2),
      Math.round(plant.maturity * 100),
      round(plant.ageYears, 1),
      containerLabel(plant.containerStage),
      Math.round(plant.health * 100),
      plant.groupName ?? '',
      plant.locked ? 'yes' : 'no',
      plant.hidden ? 'yes' : 'no',
      plant.id,
    ]);
  }
  return toCsv(rows);
}

function areaInUnit(squareMeters: number, unit: DisplayUnit): number {
  return unit === 'feet' ? squareMeters * SQUARE_FEET_PER_SQUARE_METER : squareMeters;
}
